// The boat's cameras' UniFi Protect key, in the boat's Settings (TOM-835,
// cloud ADR 0412; contract §5.26). Every owner may set, replace or remove it,
// and confirm the console's certificate.
//
// The key field is write-only: it is never filled from the server, which only
// ever says the key's last four characters, and it is emptied once saved.
// While the boat has not answered, the section asks again every 5 s.
//
// `ProtectPanelView` draws from props alone, so cloud/web's Node vitest
// renders it to static markup; `ProtectPanel` owns the requests.

import { useCallback, useEffect, useState } from 'react';

import {
  groupFingerprint,
  needsCertificateConfirm,
  parseProtectView,
  pinConfirmText,
  protectErrorMessage,
  protectStatusLine,
  protectTime,
  PROTECT_POLL_MS,
  shouldPollProtect,
  type ProtectView,
} from '../../lib/protectKey';

export interface ProtectPanelViewProps {
  boatName: string;
  view: ProtectView | null;
  loadFailed: boolean;
  host: string;
  apiKey: string;
  busy: 'save' | 'remove' | 'confirm' | null;
  error: string | null;
  onHost: (v: string) => void;
  onApiKey: (v: string) => void;
  onSave: () => void;
  onRemove: () => void;
  onConfirm: (sha256: string) => void;
  onRetry: () => void;
}

export function ProtectPanelView(props: ProtectPanelViewProps): React.ReactElement {
  const { boatName, view, busy } = props;
  const status = view !== null ? protectStatusLine(view) : null;
  const hasKey = view?.keyLast4 != null && view.configured;
  const cert = view !== null && needsCertificateConfirm(view) ? view.certificate : null;
  const pinText = view !== null ? pinConfirmText(view.pinConfirm) : null;

  return (
    <div className="panel" data-testid="boat-settings-protect">
      <h3 id="boat-settings-protect-heading">Cameras: UniFi Protect</h3>
      <p className="muted">
        Connect {boatName}&apos;s cameras by entering its UniFi Protect console and an API key from
        the console&apos;s Integrations settings. Neomaris stores the key encrypted, sends it only
        to {boatName}, and never shows it again. The key reads live video and detections, not
        recordings.
      </p>

      {props.loadFailed ? (
        <p className="alarm-form-err" role="alert" data-testid="protect-load-error">
          Couldn&apos;t load the camera settings.{' '}
          <button type="button" className="btn" onClick={props.onRetry}>
            Try again
          </button>
        </p>
      ) : null}

      {status !== null ? (
        <p className="protect-panel__status" role="status" data-testid="protect-status">
          {status}
        </p>
      ) : null}

      <form
        className="protect-panel__form"
        onSubmit={(e) => {
          e.preventDefault();
          props.onSave();
        }}
      >
        <label htmlFor="protect-host">Console address</label>
        <input
          id="protect-host"
          className="form-input"
          type="text"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          placeholder="192.168.1.1"
          maxLength={253}
          value={props.host}
          onChange={(e) => props.onHost(e.target.value)}
          data-testid="protect-host"
        />
        <p className="muted protect-panel__help">
          Leave it empty to keep the console the boat already uses.
        </p>

        <label htmlFor="protect-key">API key</label>
        <input
          id="protect-key"
          className="form-input"
          type="password"
          autoComplete="off"
          spellCheck={false}
          maxLength={256}
          placeholder={hasKey ? 'Enter a new key to replace it' : ''}
          value={props.apiKey}
          onChange={(e) => props.onApiKey(e.target.value)}
          data-testid="protect-key"
        />
        {hasKey ? (
          <p className="muted protect-panel__help" data-testid="protect-key-last4">
            Key ending ••••{view.keyLast4}
          </p>
        ) : null}

        <div className="protect-panel__actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy !== null || props.apiKey.length === 0}
            data-testid="protect-save"
          >
            {busy === 'save' ? 'Saving…' : hasKey ? 'Replace key' : 'Save key'}
          </button>
          {hasKey ? (
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={props.onRemove}
              data-testid="protect-remove"
            >
              {busy === 'remove' ? 'Removing…' : 'Remove key'}
            </button>
          ) : null}
        </div>
      </form>

      {cert !== null && cert.observedSha256 !== null ? (
        <div className="protect-panel__cert" data-testid="protect-cert">
          <h4>
            {view?.connection === 'cert_changed'
              ? 'The console’s certificate changed'
              : 'Confirm the console’s certificate'}
          </h4>
          <p className="muted">
            Open the console&apos;s own settings and check that its certificate fingerprint matches
            this one, pair by pair. Confirm only if it does: a different one may mean something else
            is answering on the boat&apos;s network.
          </p>
          <code className="protect-panel__fingerprint" data-testid="protect-fingerprint">
            {groupFingerprint(cert.observedSha256).map((line) => (
              <span key={line}>{line}</span>
            ))}
          </code>
          {cert.observedValidTo !== null ? (
            <p className="muted">Expires {protectTime(cert.observedValidTo)}</p>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy !== null || view?.pinConfirm?.phase === 'waiting'}
            onClick={() => props.onConfirm(cert.observedSha256!)}
            data-testid="protect-confirm"
          >
            {busy === 'confirm' ? 'Confirming…' : 'It matches, confirm'}
          </button>
        </div>
      ) : null}

      {pinText !== null ? (
        <p className="muted protect-panel__note" data-testid="protect-pin-status">
          {pinText}
        </p>
      ) : null}

      {props.error !== null ? (
        <p className="alarm-form-err protect-panel__note" role="alert" data-testid="protect-error">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

async function request(
  boatId: string,
  method: 'GET' | 'PUT' | 'DELETE' | 'POST',
  path = '',
  body?: unknown
): Promise<ProtectView> {
  const res = await fetch(`/v1/boats/${encodeURIComponent(boatId)}/protect${path}`, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new Error(protectErrorMessage(res.status, json));
  const view = parseProtectView(json);
  if (view === null) throw new Error(protectErrorMessage(500, null));
  return view;
}

export function ProtectPanel({
  boatId,
  boatName,
}: {
  boatId: string;
  boatName: string;
}): React.ReactElement {
  const [view, setView] = useState<ProtectView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [host, setHost] = useState('');
  const [hostTouched, setHostTouched] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState<'save' | 'remove' | 'confirm' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await request(boatId, 'GET'));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [boatId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The host field starts at the boat's host; the key field never does.
  useEffect(() => {
    if (!hostTouched && view?.host != null) setHost(view.host);
  }, [view?.host, hostTouched]);

  const polling = shouldPollProtect(view);
  useEffect(() => {
    if (!polling) return;
    const id = window.setInterval(() => void load(), PROTECT_POLL_MS);
    return () => clearInterval(id);
  }, [polling, load]);

  async function run(kind: 'save' | 'remove' | 'confirm', work: () => Promise<ProtectView>) {
    setBusy(kind);
    setError(null);
    try {
      setView(await work());
      if (kind === 'save') setApiKey('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <ProtectPanelView
      boatName={boatName}
      view={view}
      loadFailed={loadFailed}
      host={host}
      apiKey={apiKey}
      busy={busy}
      error={error}
      onHost={(v) => {
        setHostTouched(true);
        setHost(v);
      }}
      onApiKey={setApiKey}
      onSave={() =>
        void run('save', () =>
          request(
            boatId,
            'PUT',
            '',
            host.trim().length > 0 ? { host: host.trim(), apiKey } : { apiKey }
          )
        )
      }
      onRemove={() => void run('remove', () => request(boatId, 'DELETE'))}
      onConfirm={(sha256) =>
        void run('confirm', () => request(boatId, 'POST', '/confirm-certificate', { sha256 }))
      }
      onRetry={() => void load()}
    />
  );
}
